import React from 'react';
import { ScenarioBriefing as ScenarioBriefingType } from '@/types';
import { PlayArrow, Timer, Assignment, Lightbulb } from '@mui/icons-material';

interface ScenarioBriefingProps {
  title: string;
  duration: number;
  difficulty: string;
  briefing: ScenarioBriefingType;
  onStart: () => void;
  isStarting: boolean;
}

const ScenarioBriefing: React.FC<ScenarioBriefingProps> = ({
  title,
  duration,
  difficulty,
  briefing,
  onStart,
  isStarting,
}) => {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-bold mb-4">
            <Timer style={{ fontSize: '14px' }} />
            {Math.floor(duration / 60)} MINUTES &middot; {difficulty.toUpperCase()}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
          <p className="text-slate-400 text-sm">
            Your role: <span className="text-white font-semibold">{briefing.role}</span>
          </p>
        </div>

        {/* Briefing Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-6">
          {/* Situation */}
          <div className="mb-6">
            <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider mb-2">Situation</h2>
            <p className="text-white leading-relaxed">{briefing.context}</p>
          </div>

          {/* Objectives */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Assignment style={{ fontSize: '16px' }} className="text-amber-400" />
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Objectives</h2>
            </div>
            <ul className="space-y-2">
              {briefing.objectives.map((obj, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold mt-0.5">
                    {i + 1}
                  </span>
                  {obj}
                </li>
              ))}
            </ul>
          </div>

          {/* Tips */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Lightbulb style={{ fontSize: '16px' }} className="text-green-400" />
              <h2 className="text-sm font-bold text-slate-300 uppercase tracking-wider">Tips</h2>
            </div>
            <ul className="space-y-1.5">
              {briefing.tips.map((tip, i) => (
                <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                  <span className="text-green-400 mt-1">&bull;</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Start Button */}
        <button
          onClick={onStart}
          disabled={isStarting}
          className="w-full flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold text-lg py-4 rounded-xl transition-colors"
        >
          {isStarting ? (
            <>Starting...</>
          ) : (
            <>
              <PlayArrow />
              Begin Simulation
            </>
          )}
        </button>
        <p className="text-center text-xs text-slate-500 mt-3">
          The timer starts when you click Begin
        </p>
      </div>
    </div>
  );
};

export default ScenarioBriefing;
